import {
  KatButton,
  KatDateRangePicker,
  KatDropdown,
  KatIcon,
  KatPopover,
} from "@amzn/katal-react";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { TestStatus } from "src/api/interfaces/nest";
import {
  isAfterToday,
  isDateDisabledWhenDateFrom,
  isDateDisabledWhenDateTo,
} from "./dateDisablingFunctions";
import "./index.css";

interface NestHistoryDateFilterProps {
  dateFrom?: Date;
  dateTo?: Date;
  status?: TestStatus;
  onDateRangeChange: (from?: Date, to?: Date) => void;
  onStatusChange: (value?: TestStatus) => void;
}

export function NestHistoryDateFilter({
  dateFrom,
  dateTo,
  status,
  onDateRangeChange,
  onStatusChange,
}: NestHistoryDateFilterProps) {
  const { t } = useTranslation();
  const dateRangePickerRef = useRef<any>(null);

  useEffect(() => {
    if (
      !dateRangePickerRef?.current?.startPicker ||
      !dateRangePickerRef?.current?.endPicker
    ) {
      return;
    }

    dateRangePickerRef.current.endPicker.isDateDisabled = dateFrom
      ? isDateDisabledWhenDateFrom(dateFrom)
      : isAfterToday;

    dateRangePickerRef.current.startPicker.isDateDisabled = dateTo
      ? isDateDisabledWhenDateTo(dateTo)
      : isAfterToday;
  }, [dateFrom, dateTo]);

  const statusOptions = Object.values(TestStatus).map((s) => ({
    name: t(`nest_status_${s.toLocaleLowerCase()}`),
    value: s,
  }));

  return (
    <KatPopover triggerType="click" position="bottom">
      <KatIcon
        slot="trigger"
        name="filter"
        size="small"
        className="nest-filter-trigger"
      />
      <div className="nest-filter-content">
        <div className="nest-filter-row">
          <KatDateRangePicker
            ref={dateRangePickerRef}
            startLabel={t("aic_nest_history_dateFrom")}
            endLabel={t("aic_nest_history_dateTo")}
            startValue={dateFrom}
            endValue={dateTo}
            onChange={(e) =>
              onDateRangeChange(
                e.detail.startDate ? new Date(e.detail.startDate) : undefined,
                e.detail.endDate ? new Date(e.detail.endDate) : undefined
              )
            }
          />
          {(dateFrom || dateTo) && (
            <KatIcon
              name="close"
              size="small"
              className="nest-filter-clear"
              onClick={() => onDateRangeChange(undefined, undefined)}
            />
          )}
        </div>
        <div className="nest-filter-row">
          <KatDropdown
            className="nest-filter-dropdown"
            label={t("aic_nest_history_status")}
            options={statusOptions}
            value={status}
            onChange={(e) => onStatusChange(e.detail.value as TestStatus)}
          />
          {status && (
            <KatIcon
              name="close"
              size="small"
              className="nest-filter-clear"
              onClick={() => onStatusChange(undefined)}
            />
          )}
        </div>
        <KatButton
          variant="secondary"
          size="small"
          onClick={() => {
            onDateRangeChange(undefined, undefined);
            onStatusChange(undefined);
          }}
        >
          {t("aic_nest_history_clearFilter")}
        </KatButton>
      </div>
    </KatPopover>
  );
}
